// @ts-nocheck
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Platform } from '@ionic/angular';
import { AngularFirestore } from '@angular/fire/compat/firestore';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  userProperties$ = new BehaviorSubject<string[]>([]);
  private wonderPushInitializedUid: string | null = null;
  private wonderPushSubscribedUid: string | null = null;
  private isFirebaseInitialized = false;
  private messaging: any;
  private currentFCMToken: string | null = null;

  constructor(
    private platform: Platform,
    private firestore: AngularFirestore
  ) {}

  async initNotifications(uid: string): Promise<boolean> {
    try {
      if (this.platform.is('capacitor')) {
        console.log('Native WonderPush setup is handled by the mobile path');
        return false;
      }

      const wonderPushSubscribed = await this.initWonderPush(uid);
      if ('Notification' in window && Notification.permission === 'granted') {
        await this.initFirebaseFCM(uid);
      }
      console.log('Notification services initialized successfully');
      return wonderPushSubscribed;
    } catch (error) {
      console.error('Error initializing notification services:', error);
      return false;
    }
  }

  async setPushNotificationsEnabled(
    uid: string,
    enabled: boolean,
    nativeWonderPush?: any
  ): Promise<boolean> {
    const platformName = this.platform.is('capacitor') ? 'capacitor' : 'web';
    this.logPushSubscribe('toggle-requested', {
      uid,
      enabled,
      platform: platformName,
    });

    const result = enabled
      ? await this.enableWonderPushForPlatform(uid, nativeWonderPush)
      : await this.disableWonderPushForPlatform(uid, nativeWonderPush);

    this.logPushSubscribe('toggle-completed', {
      uid,
      enabled,
      platform: platformName,
      result,
    });

    return result;
  }

  async initFirebaseFCM(uid: string) {
    if (this.isFirebaseInitialized) {
      console.log('Firebase FCM already initialized');
      return;
    }

    try {
      // Only initialize FCM on web platform
      if (this.platform.is('capacitor')) {
        console.log('FCM not supported on mobile, using WonderPush only');
        return;
      }

      console.log('Initializing Firebase FCM for user:', uid);

      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return;
      }

      // Request notification permission first
      let permission = Notification.permission;
      if (permission === 'default') {
        console.log('Requesting notification permission...');
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return;
      }

      console.log('Notification permission granted, getting FCM token...');

      this.messaging = getMessaging();

      // Request token with VAPID key
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebaseConfig.vapidKey,
      });

      if (token) {
        console.log('FCM token received:', token);
        this.currentFCMToken = token;

        // Register token with Firestore
        await this.registerFCMToken(token, 'web');

        // Listen for foreground messages
        onMessage(this.messaging, payload => {
          console.log('Foreground message received:', payload);
          this.handleForegroundMessage(payload);
        });

        this.isFirebaseInitialized = true;
        console.log('Firebase FCM initialized successfully');
      } else {
        console.log('No FCM token available - registration may have failed');
      }
    } catch (error) {
      console.error('Error initializing Firebase FCM:', error);
      this.isFirebaseInitialized = false;
    }
  }

  async initWonderPush(uid: string): Promise<boolean> {
    if (this.wonderPushSubscribedUid === uid) {
      return true;
    }

    try {
      window.WonderPush = window.WonderPush || [];

      if (this.wonderPushInitializedUid !== uid) {
        WonderPush.push([
          'init',
          {
            webKey: environment.WONDERPUSH.WEBTOKEN,
            userId: uid,
          },
        ]);
        this.wonderPushInitializedUid = uid;
      }

      await WonderPush.push(['setUserConsent', true]);
      const subscribed = await this.subscribe(uid);
      if (subscribed) {
        this.wonderPushSubscribedUid = uid;
        console.log('WonderPush initialized successfully for user:', uid);
      }
      return subscribed;
    } catch (error) {
      console.error('Error initializing WonderPush:', error);
      if (this.wonderPushInitializedUid === uid) {
        this.wonderPushInitializedUid = null;
      }
      await this.recordWonderPushSubscriptionStatus(uid, false, error, 'web');
      return false;
    }
  }

  async registerFCMToken(token: string, platform: string) {
    try {
      console.log(
        'Attempting to register FCM token:',
        token.substring(0, 20) + '...'
      );

      // Get current user ID
      const auth = (await import('firebase/auth')).getAuth();
      const user = auth.currentUser;

      if (!user) {
        console.error('No authenticated user found for token registration');
        return;
      }

      console.log('Registering token for user:', user.uid);

      // Store token directly in Firestore
      await this.firestore
        .collection('users')
        .doc(user.uid)
        .collection('tokens')
        .doc(token)
        .set({
          platform: platform || 'unknown',
          createdAt: new Date(),
          lastUsed: new Date(),
        });

      console.log(
        'FCM token registered successfully in Firestore for user:',
        user.uid
      );

      // Verify the token was stored
      const tokenDoc = await this.firestore
        .collection('users')
        .doc(user.uid)
        .collection('tokens')
        .doc(token)
        .get()
        .toPromise();

      if (tokenDoc.exists) {
        console.log(
          'Token verification successful - token exists in Firestore'
        );
      } else {
        console.error(
          'Token verification failed - token not found in Firestore'
        );
      }
    } catch (error) {
      console.error('Error registering FCM token:', error);
    }
  }

  async unregisterFCMToken() {
    if (!this.currentFCMToken) {
      return;
    }

    try {
      // Get current user ID
      const auth = (await import('firebase/auth')).getAuth();
      const user = auth.currentUser;

      if (!user) {
        console.error('No authenticated user found');
        return;
      }

      // Remove token directly from Firestore
      await this.firestore
        .collection('users')
        .doc(user.uid)
        .collection('tokens')
        .doc(this.currentFCMToken)
        .delete();

      console.log('FCM token unregistered successfully');
      this.currentFCMToken = null;
    } catch (error) {
      console.error('Error unregistering FCM token:', error);
    }
  }

  handleForegroundMessage(payload: any) {
    // Handle foreground messages (when app is open)
    const { notification, data } = payload;

    if (notification) {
      // Show custom notification or update UI
      this.showCustomNotification(notification.title, notification.body, data);
    }
  }

  showCustomNotification(title: string, body: string, data: any) {
    // Create custom notification UI or use browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/assets/icon/favicon.png',
        badge: '/assets/icon/favicon.png',
        data,
      });

      notification.onclick = () => {
        // Handle notification click
        if (data?.targetUrl) {
          window.open(data.targetUrl, '_blank');
        }
        notification.close();
      };
    }
  }

  async initNativeWonderPush(uid: string, wonderPush: any): Promise<boolean> {
    try {
      this.logPushSubscribe('native-subscribe-start', { uid });
      await wonderPush.setUserConsent(true);
      await wonderPush.setUserId(uid);
      await wonderPush.subscribeToNotifications();
      const subscribed = await wonderPush.isSubscribedToNotifications();

      await this.recordWonderPushSubscriptionStatus(
        uid,
        !!subscribed,
        subscribed ? null : 'Native WonderPush subscription was not confirmed',
        'capacitor',
        !!subscribed
      );

      if (subscribed) {
        this.getUserProperties(wonderPush, 'capacitor');
      }

      this.logPushSubscribe('native-subscribe-complete', {
        uid,
        subscribed: !!subscribed,
      });
      return !!subscribed;
    } catch (error) {
      console.error('Error during native WonderPush subscription:', error);
      this.logPushSubscribe('native-subscribe-error', {
        uid,
        error: this.sanitizePushError(error),
      });
      await this.recordWonderPushSubscriptionStatus(
        uid,
        false,
        error,
        'capacitor',
        false
      );
      return false;
    }
  }

  async subscribe(uid: string): Promise<boolean> {
    try {
      const isSubscribed = await new Promise<boolean>(resolve => {
        WonderPush.push(async () => {
          try {
            WonderPush.setUserId(uid);
            await WonderPush.subscribeToNotifications();
            const subscribed = await WonderPush.isSubscribedToNotifications();
            resolve(!!subscribed);
          } catch (error) {
            console.error('Error during WonderPush subscription:', error);
            resolve(false);
          }
        });
      });

      await this.recordWonderPushSubscriptionStatus(
        uid,
        isSubscribed,
        isSubscribed ? null : 'WonderPush subscription was not confirmed',
        'web',
        isSubscribed
      );

      if (isSubscribed) {
        this.getUserProperties(WonderPush, 'web');
        console.log(
          'Successfully subscribed to WonderPush notifications for user:',
          uid
        );
      } else {
        console.warn(
          'WonderPush subscription was not confirmed for user:',
          uid
        );
      }

      return isSubscribed;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      await this.recordWonderPushSubscriptionStatus(uid, false, error, 'web', false);
      return false;
    }
  }

  async recordWonderPushSubscriptionStatus(
    uid: string,
    subscribed: boolean,
    error?: any,
    platform?: string,
    pushNotificationsEnabled: boolean = subscribed
  ) {
    const errorMessage =
      error && typeof error === 'object' && 'message' in error
        ? error.message
        : typeof error === 'string'
          ? error
          : null;

    await this.firestore
      .collection('users')
      .doc(uid)
      .set(
        {
          notificationSettings: {
            wonderPushSubscribed: subscribed,
            pushNotificationsEnabled,
            wonderPushLastCheckedAt: new Date(),
            wonderPushPlatform: platform || null,
            wonderPushLastError: subscribed ? null : errorMessage,
          },
        },
        { merge: true }
      );
  }

  private async enableWonderPushForPlatform(
    uid: string,
    nativeWonderPush?: any
  ): Promise<boolean> {
    if (this.platform.is('capacitor')) {
      if (!nativeWonderPush) {
        await this.recordWonderPushSubscriptionStatus(
          uid,
          false,
          'Native WonderPush plugin unavailable',
          'capacitor',
          false
        );
        return false;
      }

      return this.initNativeWonderPush(uid, nativeWonderPush);
    }

    return this.initNotifications(uid);
  }

  private async disableWonderPushForPlatform(
    uid: string,
    nativeWonderPush?: any
  ): Promise<boolean> {
    const platformName = this.platform.is('capacitor') ? 'capacitor' : 'web';

    try {
      this.logPushSubscribe('unsubscribe-start', {
        uid,
        platform: platformName,
      });

      if (this.platform.is('capacitor')) {
        await nativeWonderPush?.unsubscribeFromNotifications?.();
      } else {
        WonderPush.unsubscribeFromNotifications();
        WonderPush.push(['unsetUserId']);
      }

      if (this.wonderPushSubscribedUid === uid) {
        this.wonderPushSubscribedUid = null;
      }

      await this.recordWonderPushSubscriptionStatus(
        uid,
        false,
        null,
        platformName,
        false
      );

      this.logPushSubscribe('unsubscribe-complete', {
        uid,
        platform: platformName,
      });
      return true;
    } catch (error) {
      this.logPushSubscribe('unsubscribe-error', {
        uid,
        platform: platformName,
        error: this.sanitizePushError(error),
      });
      await this.recordWonderPushSubscriptionStatus(
        uid,
        false,
        error,
        platformName,
        false
      );
      return false;
    }
  }

  private logPushSubscribe(source: string, details: any) {
    if (environment.production) {
      return;
    }

    console.info(
      `[push-subscribe] ${JSON.stringify({
        source,
        ...details,
      })}`
    );
  }

  private sanitizePushError(error: any) {
    return error && typeof error === 'object' && 'message' in error
      ? error.message
      : typeof error === 'string'
        ? error
        : null;
  }

  async unsubscribe() {
    // Unregister FCM token
    await this.unregisterFCMToken();

    // Unsubscribe from WonderPush
    WonderPush.unsubscribeFromNotifications();
    WonderPush.push(['unsetUserId']);
  }

  async removeUserData() {
    // Unregister FCM token
    await this.unregisterFCMToken();

    // Clear WonderPush data
    WonderPush.unsubscribeFromNotifications();
    WonderPush.push(['clearAllData']);
    WonderPush.push(['unsetUserId']);
  }

  updateUsersProperties(props) {
    let userProperties = this.userProperties$.value;
    if (userProperties.includes(props.title)) {
      userProperties = userProperties.filter(item => item !== props.title);
    } else {
      userProperties.push(props.title);
    }
    this.userProperties$.next(userProperties);
    WonderPush.push([
      'putProperties',
      {
        string_merfolkTopics: userProperties,
      },
    ]);
  }

  getUserProperties(wonderPush, platform) {
    //!  settimeout is chosen because the push method don't see 'this'
    setTimeout(async () => {
      const props = await wonderPush.getPropertyValues('string_merfolkTopics');
      this.userProperties$.next(props);

      const defaultProperties = [
        'test',
        'merfolk',
        'events',
        'likes',
        'comments',
        'daily_challenges',
      ];
      if (!props.length && platform !== 'capacitor') {
        wonderPush.push([
          'putProperties',
          {
            string_merfolkTopics: defaultProperties,
          },
        ]);
        this.getUserProperties(wonderPush, 'web');
      } else if (!props.length && platform === 'capacitor') {
        wonderPush.putProperties({
          string_merfolkTopics: defaultProperties,
        });
        this.getUserProperties(wonderPush, 'capacitor');
      }
    }, 0);
  }
}

